import axios, { AxiosInstance } from "axios";
import { authPersistence } from "../utils/authPersistence";
import { trackAxiosError } from "./appInsights";
import {
	apiScopes,
	msalInstance,
	recoverExpiredMsalSession,
	startLoginRedirect,
	tryPromptNoneReauth,
} from "./authConfig";

// Create an Axios instance
const apiClient = axios.create({
	baseURL: import.meta.env.VITE_API_BACKEND_EINSTEIN_URL,
	headers: {
		"Content-Type": "application/json",
	},
});

export const abacusApiClient = axios.create({
	baseURL: import.meta.env.VITE_BACKEND_ABACUS_URL,
	headers: {
		"Content-Type": "application/json",
	},
});

// Add a request interceptor for all clients
const addAuthInterceptor = (client: AxiosInstance) => {
	client.interceptors.request.use(
		(config) => {
			const token = apiClient.defaults.headers.common.Authorization;
			if (token) {
				config.headers.Authorization = token;
			}
			return config;
		},
		(error: unknown) => {
			return Promise.reject(error);
		},
	);
};

addAuthInterceptor(apiClient);
addAuthInterceptor(abacusApiClient);

// Add a response interceptor for both clients
const addResponseInterceptor = (client: AxiosInstance) => {
	client.interceptors.response.use(
		(response) => {
			return response;
		},
		async (error) => {
			const originalRequest = error.config;
			const shouldTrackFailure =
				!error.response ||
				error.response.status !== 401 ||
				originalRequest?._retry;

			// Handle 401 Unauthorized errors
			if (error.response?.status === 401 && !originalRequest._retry) {
				originalRequest._retry = true;
				const account = msalInstance.getActiveAccount();

				try {
					if (account) {
						// Try to acquire token silently with force refresh
						const tokenResponse = await msalInstance.acquireTokenSilent({
							scopes: apiScopes,
							account,
							forceRefresh: true,
						});

						// Update the authorization header with new token
						const newToken = `Bearer ${tokenResponse.accessToken}`;
						apiClient.defaults.headers.common.Authorization = newToken;
						abacusApiClient.defaults.headers.common.Authorization = newToken;
						originalRequest.headers.Authorization = newToken;

						// Retry the original request with new token
						return client(originalRequest);
					}
				} catch (tokenError) {
					// If the browser session still exists, do one top-level prompt=none reauth.
					if (await recoverExpiredMsalSession(tokenError)) {
						const loginHint =
							account?.username || authPersistence.getSavedAccount()?.username;

						if (await tryPromptNoneReauth(loginHint)) {
							return new Promise(() => {});
						}

						console.error(
							"[Auth] Silent reauthentication unavailable, redirecting to login",
						);
						await startLoginRedirect({
							loginHint,
							prompt: "select_account",
						});
						return new Promise(() => {});
					}
				}
			}

			// Handle network and server errors
			const isNetworkError = error.code === "ERR_NETWORK";
			const isServerError = error.response && error.response.status === 500;
			const isOnServerDownPage = window.location.pathname === "/server-down";

			if ((isNetworkError || isServerError) && !isOnServerDownPage) {
				// console.log('Server is down');
				// window.location.href = '/server-down';
			}

			if (shouldTrackFailure) {
				trackAxiosError(error);
			}

			return Promise.reject(error);
		},
	);
};

addResponseInterceptor(apiClient);
addResponseInterceptor(abacusApiClient);

export default apiClient;
